import { Box, CircularProgress, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import AvatarChatHistory, {
  IAvatarChatHistoryMessage
} from "./AvatarChatHistory";
import { ChatTextInput } from "./ChatTextInput";

interface IAvatarChatBoxProps {
  messages: IAvatarChatHistoryMessage[];
  disabled?: boolean;
  loading?: boolean;
  text: string;
  setText: (value: string) => void;
  handleSpeak: () => void;
  handleInterrupt?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleOpenSummary: () => void;
  handleRecord: (blob: Blob) => void;
  audioBlob?: Blob | null;
  handleDeleteAudio: () => void;
  onScrollHistory?: (event: React.UIEvent<HTMLDivElement>) => void;
  autoScrollToBottom?: boolean;
  isHistoryLoading?: boolean;
}

const AvatarChatBox = ({
  messages,
  disabled = false,
  loading = false,
  text,
  setText,
  handleSpeak,
  handleInterrupt = undefined,
  handleOpenSummary,
  handleRecord,
  audioBlob,
  handleDeleteAudio,
  onScrollHistory,
  autoScrollToBottom = true,
  isHistoryLoading = false
}: IAvatarChatBoxProps) => {
  const { t } = useTranslation();
  const chatHistoryRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!autoScrollToBottom) {
      return;
    }
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  }, [messages, autoScrollToBottom]);

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}
    >
      {/* History */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          mb: 1
        }}
        ref={chatHistoryRef}
        onScroll={onScrollHistory}
      >
        {isHistoryLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 1
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="caption" sx={{ ml: 1 }}>
              Cargando mensajes...
            </Typography>
          </Box>
        )}
        <AvatarChatHistory messages={messages} />
      </Box>

      {/* Chat input */}
      <Box sx={{ width: "97%" }}>
        <ChatTextInput
          disabled={disabled}
          input={text}
          label="Chat"
          loading={loading}
          placeholder={t("chatbot.actions.interact")}
          setInput={setText}
          onSubmit={handleSpeak}
          handleInterrupt={handleInterrupt}
          handleOpenSummary={handleOpenSummary}
          onRecord={handleRecord}
          audioBlob={audioBlob}
          onDeleteAudio={handleDeleteAudio}
        />
      </Box>
    </Box>
  );
};

export default AvatarChatBox;

